import { useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import type { ReceptionTerminalSpec } from "../scenario/types";

const DEG = Math.PI / 180;

/** Screen-space pin (percent of viewport) for the "switch on" hotspot. */
export type TerminalPin = { xPct: number; yPct: number; visible: boolean; distance: number };

/** Idle "tap to begin" welcome graphic drawn on the kiosk screen (CSP-safe canvas). */
function makeIdleTexture(label: string): THREE.CanvasTexture {
  const w = 512;
  const h = 320;
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const g = cv.getContext("2d")!;
  const grad = g.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, "#132323");
  grad.addColorStop(1, "#0a1414");
  g.fillStyle = grad;
  g.fillRect(0, 0, w, h);
  // Power glyph
  g.strokeStyle = "rgba(120,220,205,0.85)";
  g.lineWidth = 6;
  g.lineCap = "round";
  g.beginPath();
  g.moveTo(w / 2, h * 0.28);
  g.lineTo(w / 2, h * 0.46);
  g.stroke();
  g.beginPath();
  g.arc(w / 2, h * 0.42, 46, 0.75 * Math.PI, 2.25 * Math.PI);
  g.stroke();
  g.fillStyle = "rgba(230,255,250,0.92)";
  g.textAlign = "center";
  g.font = "600 34px Segoe UI, Arial, sans-serif";
  g.fillText(label, w / 2, h * 0.72);
  g.fillStyle = "rgba(180,225,218,0.55)";
  g.font = "20px Segoe UI, Arial, sans-serif";
  g.fillText("touch to switch on", w / 2, h * 0.84);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

/**
 * A modelled welcome-kiosk terminal: round brushed-metal base, slim neck,
 * tilted screen housing with a glowing "power ring" and an idle screen
 * texture. Physically sits on a desk/counter surface (`spec.position` is a
 * point ON the surface, not the floor). Projects the screen centre to
 * viewport % each frame via `onPin` so App can render a proximity hotspot.
 */
export function ReceptionTerminal({
  spec,
  onPin,
}: {
  spec: ReceptionTerminalSpec;
  onPin: (pin: TerminalPin) => void;
}) {
  const { camera } = useThree();
  const screenAnchor = useRef<THREE.Group>(null);
  const worldPos = useRef(new THREE.Vector3()).current;
  const camDir = useRef(new THREE.Vector3()).current;
  const toPin = useRef(new THREE.Vector3()).current;
  const last = useRef<TerminalPin>({ xPct: 0, yPct: 0, visible: false, distance: 999 });
  const onPinRef = useRef(onPin);
  onPinRef.current = onPin;

  const scale = spec.scale ?? 1;
  const yaw = (spec.rotationDeg ?? 0) * DEG;

  const idleTex = useMemo(() => {
    if (spec.imageUrl) {
      const tex = new THREE.TextureLoader().load(spec.imageUrl);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = 8;
      return tex;
    }
    return makeIdleTexture(spec.label ?? "Welcome");
  }, [spec.imageUrl, spec.label]);

  useFrame(() => {
    const anchor = screenAnchor.current;
    if (!anchor) return;
    anchor.getWorldPosition(worldPos);
    camera.getWorldDirection(camDir);
    toPin.copy(worldPos).sub(camera.position);
    const distance = toPin.length();
    const inFront = toPin.normalize().dot(camDir) > 0.1;
    const proj = worldPos.clone().project(camera);
    const xPct = (proj.x * 0.5 + 0.5) * 100;
    const yPct = (-proj.y * 0.5 + 0.5) * 100;
    const onScreen = xPct > -5 && xPct < 105 && yPct > -5 && yPct < 105;
    const pin: TerminalPin = { xPct, yPct, visible: inFront && onScreen && distance < 5, distance };
    const p = last.current;
    if (
      p.visible !== pin.visible ||
      Math.abs(p.xPct - pin.xPct) > 0.3 ||
      Math.abs(p.yPct - pin.yPct) > 0.3 ||
      Math.abs(p.distance - pin.distance) > 0.05
    ) {
      last.current = pin;
      onPinRef.current(pin);
    }
    if (typeof window !== "undefined") {
      (window as unknown as { __terminalPin?: TerminalPin }).__terminalPin = pin;
    }
  });

  // Group origin = the counter/desk surface point. Everything builds UP from y=0.
  const BASE_H = 0.03;
  const NECK_H = 0.3;
  // Screen is VERTICAL (faces +Z, no tilt). This is required so the Scene-1
  // camera can sit dead-on perpendicular and the DOM terminal overlay lines up
  // EXACTLY with the 3D screen face (see the --scr-* fit in styles.css /
  // wp_terminal_close). A tilt would make the overlay drift at other viewport
  // sizes.
  const SCREEN_TILT = 0;
  return (
    <group position={spec.position} rotation={[0, yaw, 0]} scale={scale}>
      {/* Round brushed-metal base */}
      <mesh position={[0, BASE_H / 2, 0]}>
        <cylinderGeometry args={[0.135, 0.15, BASE_H, 32]} />
        <meshStandardMaterial color="#2c3138" metalness={0.75} roughness={0.28} />
      </mesh>
      {/* Glowing power ring set into the base front */}
      <mesh position={[0, BASE_H + 0.001, 0.1]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.028, 0.038, 32]} />
        <meshStandardMaterial
          color="#4aa89c"
          emissive="#4aa89c"
          emissiveIntensity={1.4}
          toneMapped={false}
        />
      </mesh>
      {/* Neck */}
      <mesh position={[0, BASE_H + NECK_H / 2, -0.03]}>
        <cylinderGeometry args={[0.022, 0.03, NECK_H, 16]} />
        <meshStandardMaterial color="#20242b" metalness={0.7} roughness={0.3} />
      </mesh>
      {/* Screen housing, tilted back slightly like a check-in kiosk */}
      <group position={[0, BASE_H + NECK_H + 0.155, -0.04]} rotation={[SCREEN_TILT, 0, 0]}>
        {/* Bezel */}
        <mesh>
          <boxGeometry args={[0.32, 0.22, 0.028]} />
          <meshStandardMaterial color="#14171c" metalness={0.5} roughness={0.4} />
        </mesh>
        {/* Screen face (emissive, reads as "on" even in soft ambient light) */}
        <group ref={screenAnchor} position={[0, 0, 0.016]}>
          <mesh>
            <planeGeometry args={[0.286, 0.186]} />
            <meshStandardMaterial
              map={idleTex}
              emissive="#ffffff"
              emissiveMap={idleTex}
              emissiveIntensity={0.6}
              toneMapped={false}
            />
          </mesh>
        </group>
      </group>
    </group>
  );
}
